import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { get, post } from "@utils/axios_api";
import { toast } from 'react-toastify';

const fetchEmployeeList = async (params) => {
  const response = await get("/user/comp/employee/employeeList", {
    params,
    headers: { 'Content-type': 'application/json' }
  });

  return response.result;
};

export function useEmployeeList(compCode) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const defaultCompCode = compCode;



  // 회사 정보 조회 (사원이 없어도 회사명을 표시하기 위해)
  const { data: companyData } = useQuery({
    queryKey: ["companyInfo", compCode],
    queryFn: () => get(`/user/corp/${compCode}`),
    enabled: !!compCode,
    staleTime: 5 * 60 * 1000, // 5분간 캐시
  });

  const initialEmployee = {
    compCode: defaultCompCode,
    empCode: "",
    empName: "",
    siteCode: "",
    siteName: "",
    deptCode: "",
    deptName: "",
    posCode: "",
    posName: "",
    safeRole: "",
    roleName: "",
    phone: "",
    notice: "",
    email: "",
    roleId: "R3",
    isNew: true,
    isEdited: false,
  };

  const methods = useForm({
    defaultValues: {
      searchSite: "",
      searchDept: "",
      searchPos: "",
      searchRole: "",
      searchName: "",
    },
    shouldFocusError: false,
  });

  const { setValue } = methods;

  // URL 파라미터에서 검색 조건 추출
  const searchSite = searchParams.get("searchSite") || "";
  const searchDept = searchParams.get("searchDept") || "";
  const searchPos = searchParams.get("searchPos") || "";
  const searchRole = searchParams.get("searchRole") || "";
  const searchName = searchParams.get("searchName") || "";
  const pageIndex = parseInt(searchParams.get("pageIndex") || "1", 10);
  const recordCountPerPage = parseInt(searchParams.get("pageSize") || "10", 10);

  // React Hook Form에 URL 파라미터 값 동기화
  useEffect(() => {
    setValue("searchSite", searchSite);
    setValue("searchDept", searchDept);
    setValue("searchPos", searchPos);
    setValue("searchRole", searchRole);
    setValue("searchName", searchName);
  }, [searchSite, searchDept, searchPos, searchRole, searchName, setValue]);

  // 쿼리 파라미터 구성
  const queryParam = {
    compCode: defaultCompCode,
    searchSite,
    searchDept,
    searchPos,
    searchRole,
    searchName,
    pageIndex,
    recordCountPerPage,
    pageSize: recordCountPerPage,
    firstIndex: (pageIndex - 1) * recordCountPerPage,
    lastIndex: pageIndex * recordCountPerPage,
  };



  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["employeeList", queryParam],
    queryFn: () => fetchEmployeeList(queryParam),
    enabled: !!compCode,  // 회사가 선택되지 않으면 조회하지 않음
    keepPreviousData: true,
    staleTime: 0, // 항상 최신 데이터 조회
    cacheTime: 0, // 캐시 사용 안함
  });

  const refreshList = () => {
    refetch();
  };

  // 폼 제출 처리 (조회 버튼 클릭)
  const onSubmit = (values) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("searchSite", values.searchSite || "");
    newParams.set("searchDept", values.searchDept || "");
    newParams.set("searchPos", values.searchPos || "");
    newParams.set("searchRole", values.searchRole || "");
    newParams.set("searchName", values.searchName || "");
    newParams.set("pageIndex", "1"); // 검색 시 첫 페이지로

    setSearchParams(newParams);
  };

  const handlePageChange = (targetPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageIndex", targetPage);
    setSearchParams(newParams);
  };

  const changePageSize = (size) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("pageSize", String(size));
    newParams.set("pageIndex", "1");
    setSearchParams(newParams);
  };

  const handleSelectAll = (e) => {
    if (!data || !data.resultList) return;
    const { checked } = e.target;
    if (checked) {
      const allIds = data.resultList.map((item) => item.empCode);
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (empCode) => {
    if (selectedItems.includes(empCode)) {
      setSelectedItems(selectedItems.filter((id) => id !== empCode));
    } else {
      setSelectedItems([...selectedItems, empCode]);
    }
  };

  const selectedDelete = async () => {
    try {

      const payload = selectedItems.map(empCode => ({ empCode }));

      const response = await post('/user/comp/employee/delete', payload, {
        headers: { 'Content-type': 'application/json' }
      });


      toast.success(response.message);
      setSelectedItems([]);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ["accountList"] });

    } catch (error) {
      console.error("삭제 오류:", error);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();

    if (selectedItems.length === 0) {
      toast.warn("삭제할 항목을 선택해주세요.");
      return;
    }
    if (!window.confirm("삭제 하시겠습니까?")) return;
    selectedDelete();
  };

  // 체크 여부
  const isAllChecked = !!(data?.resultList && data.resultList.length > 0 &&
      selectedItems.length === data.resultList.length);


  return {
    methods,
    data,
    companyData, // 회사 정보 추가
    isLoading,
    isError,
    error,
    onSubmit,
    handlePageChange,
    changePageSize,
    pageIndex,
    recordCountPerPage,
    refetch,
    refreshList,
    selectedItems,
    handleSelectAll,
    handleSelectItem,
    handleDelete,
    isAllChecked,
    initialEmployee,
    setValue,
  };
}